// ─── What counts as geopolitical ─────────────────────────────────────────────
//
// The classifier (a language model) had turned "gov_regulation" into a
// catch-all: 55 of 63 events in the week to 26.09 were in it, among them
// "Apple Plans a Health App Overhaul" and "Why Microsoft Stock Is Up Today".
// A prompt is a request, so the rule is also CHECKED here (David, 2026-09-26):
//
//   geopolitical = governments, regulation, tariffs, sanctions, war,
//                  elections, central banks
//
// An event keeps its category only when its headline or summary names one of
// these. A Federal Register document is a government act by definition and
// always passes. Everything else is read as "none" · not shown as geopolitical,
// not counted, not tied to anyone's portfolio. To change the rule, edit the
// lists below.

import { isPressRelease } from "../utils/newsMatch";

export const GEO_TERMS_EN = [
  // governments and their acts
  "government", "(?:trump|biden) administration", "the administration", "white house", "congress", "senate", "parliament", "ministry", "minister",
  "president (?:trump|xi|putin|biden)", "trump", "xi jinping", "beijing", "kremlin", "treaty",
  // Not "Nvidia GTC summit": a summit of governments only.
  "(?:trump|xi|biden|putin|g7|g20|nato|leaders|u\\.?s\\.?-china|us-china) summit", "summit (?:with|between|of) (?:leaders|trump|xi)",
  "executive order", "legislation", "lawmakers",
  // Not "Bill Ackman": a bill in a legislature.
  "bill (?:passes|passed|signed|vote|would|to ban)", "(?:senate|house|spending|tax|defense) bill", "ban(?:s|ned)?",
  // regulation
  "regulat\\w*", "antitrust", "ftc", "doj", "department of justice", "sec (?:charges|probe|rule)", "eu commission", "european commission",
  // trade
  "tariffs?", "duties", "antidumping", "export controls?", "import ban", "trade (?:war|deal|talks|tensions?|truce)",
  // sanctions
  "sanctions?", "embargo",
  // war and security
  "war", "military", "missiles?", "invasion", "troops", "ceasefire", "armed conflict", "nato", "defen[cs]e ministry", "strikes? on",
  // elections
  "elections?", "referendum",
  // central banks
  "central bank", "federal reserve", "the fed\\b", "fed (?:rate|chair|decision|minutes)", "fomc", "ecb", "bank of japan", "boj", "bank of england", "pboc", "rate (?:cut|hike)",
];

export const GEO_TERMS_HE = [
  "ממשלה", "ממשל", "הבית הלבן", "קונגרס", "סנאט", "פרלמנט", "פסגה",
  "רגולציה", "רגולטור", "הגבל\\S*", "מכס\\S*", "סנקצי\\S*", "אמברגו",
  "מלחמה", "צבא", "צבאי\\S*", "טיל\\S*", "הפסקת אש", "עימות",
  "בחירות", "משאל",
  "בנק מרכזי", "הפדרל ריזרב", "הפד", "הורדת ריבית", "העלאת ריבית", "החלטת ריבית",
];

const EN_RE = new RegExp(`\\b(?:${GEO_TERMS_EN.join("|")})\\b`, "i");
const HE_RE = new RegExp(`(^|[^\\u0590-\\u05FF])[והשכלבמ]?(?:${GEO_TERMS_HE.join("|")})(?=$|[^\\u0590-\\u05FF])`);

/** Does this geo_events row really describe a geopolitical event? */
export function isGeopolitical(ev) {
  if (!ev || !ev.category || ev.category === "none") return false;
  if (ev.source_type === "official") return true;
  // A press release is an advertisement, not an event (utils/newsMatch.js).
  if (isPressRelease({ source: ev.source_name, url: ev.source_url, title: ev.headline, excerpt: ev.excerpt })) return false;
  // Nor is a paid market-research report ("… Market Analysis Report 2026-2031").
  if (/\bmarket\b.*\b(report|assessment|analysis|forecast|outlook)\b.*\b20\d\d\s*[-–]\s*20\d\d\b/i.test(ev.headline || "")) return false;
  const en = `${ev.headline || ""} ${ev.excerpt || ""}`;
  const he = `${ev.headline_he || ""} ${ev.summary_he || ""}`;
  return EN_RE.test(en) || HE_RE.test(he);
}
